import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useLocationContext } from "../../contexts/locationContext";
import { handleUserRegister } from "../../shared/controllers/authController";
import {
    handleGetCountries,
    handleGetStatesByCountry,
} from "../../shared/controllers/generalController";

import authbg from "./assets/authbg.svg";
import load from "./assets/load.gif";
import logo from "./assets/logo.svg";

interface Country {
    id: string | number;
    name: string;
    iso3: string;
}

interface State {
    id: string | number;
    name: string;
}

interface FormErrors {
    name?: string;
    username?: string;
    phone?: string;
    email?: string;
    password?: string;
    country?: string;
    state?: string;
}

const Signup: React.FC = () => {
    const [name, setName] = useState<string>("");
    const [username, setUsername] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingCountries, setLoadingCountries] = useState<boolean>(false);
    const [loadingStates, setLoadingStates] = useState<boolean>(false);
    const [errors, setErrors] = useState<FormErrors>({});
    const [countries, setCountries] = useState<Country[]>([]);
    const [states, setStates] = useState<State[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
    const [selectedState, setSelectedState] = useState<string>("");
    const [referralCode, setReferralCode] = useState<string>("");
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams] = useSearchParams();
    const { location, updateLocation } = useLocationContext();

    const validateFields = (): boolean => {
        const newErrors: FormErrors = {};
        if (!name) newErrors.name = "Name is required";
        if (!username) newErrors.username = "Username is required";
        if (!phone) newErrors.phone = "Phone is required";
        if (!email) newErrors.email = "Email is required";
        if (!password) newErrors.password = "Password is required";
        if (!selectedCountry) newErrors.country = "Country is required";
        if (selectedCountry && states.length && !selectedState) newErrors.state = "State is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const extractArrayData = (data: unknown, fallback: unknown[] = []) => {
        if (data && typeof data === "object" && "data" in data && Array.isArray((data as { data: unknown }).data)) {
            return (data as { data: unknown[] }).data;
        }
        if (Array.isArray(data)) return data;
        return fallback;
    };

    useEffect(() => {
        const fetchCountries = async () => {
            setLoadingCountries(true);
            try {
                const data = await handleGetCountries();
                if (data) {
                    setCountries(extractArrayData(data) as Country[]);
                }
            } catch (error) {
                console.error("Error fetching countries:", error);
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        if (!selectedCountry?.id) {
            setStates([]);
            setSelectedState("");
            return;
        }

        setStates([]);

        const loadStatesForCountry = async () => {
            setLoadingStates(true);
            try {
                const response = await handleGetStatesByCountry(selectedCountry.id);
                setStates(extractArrayData(response));
            } catch (error) {
                console.error("Error fetching states:", error);
            } finally {
                setLoadingStates(false);
            }
        };

        loadStatesForCountry();
    }, [selectedCountry]);

    useEffect(() => {
        const storedItem = localStorage.getItem("saca_token");
        if (storedItem) {
            navigate("/dashboard");
            enqueueSnackbar("Logged in Successfully", { variant: "success" });
        }
        // Extract ref from URL and set referralCode
        const urlRef = searchParams.get("ref");
        if (urlRef) {
            setReferralCode(urlRef);
        }
    }, [enqueueSnackbar, navigate, searchParams]);

    const onSuccess = () => {
        setLoading(false);
        updateLocation({
            countryCode: selectedCountry?.iso3 || location.countryCode,
            countryName: selectedCountry?.name || location.countryName,
            state: selectedState || location.state,
        });
        localStorage.setItem("user_email", email);
        navigate("/verify-mail");
        enqueueSnackbar("Please verify your mail", { variant: "success" });
    };

    const onError = () => setLoading(false);

    const handleSubmit = () => {
        if (validateFields()) {
            setLoading(true);
            const userData = {
                name,
                username,
                email,
                phone,
                country: selectedCountry?.name || location.countryName || "",
                country_code: selectedCountry?.iso3 || location.countryCode || "",
                state: selectedState || "",
                password,
                registration_mode: "RegisterPage",
            };
            handleUserRegister(userData, referralCode || "", onSuccess, onError);
        }
    };

    const renderStateOptions = () => {
        if (loadingStates) {
            return <option value="">Loading states...</option>;
        }
        if (states.length > 0) {
            return states.map((state) => (
                <option key={state.id} value={state.name}>
                    {state.name}
                </option>
            ));
        }
        return <option value="">No states found</option>;
    };

    return (
        <div
            style={{
                backgroundImage: `url(${authbg})`,
                backgroundPosition: "center",
                backgroundSize: "cover",
            }}
            className="w-full min-h-screen bg-[#1212128d] z-[999] p-6 flex justify-center"
        >
            <div className="bg-[#FFFFFF] p-6 md:p-8 rounded-[15px] w-full max-w-[640px] shadow-lg self-start mt-10 md:mt-16">
                <div className="w-full flex flex-col items-center gap-6">
                    <img src={logo} className="w-[20%]" alt="" />
                    <p className="text-center font-Manrope font-bold text-[#272D37] text-3xl">
                        Join Us!
                    </p>

                    <div className="w-full flex flex-col gap-5">
                        <label
                            htmlFor="name"
                            className="flex flex-col w-full font-Manrope font-semibold text-sm text-[#272D37]"
                        >
                            Full Name{" "}
                            <input
                                type="text"
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="mt-2 border border-[#DAE0E6] px-4 py-3 rounded-md font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                                placeholder="Enter your Full Name"
                            />
                            {errors.name && (
                                <span className="text-red-500 text-xs font-Manrope mt-2">
                                    {errors.name}
                                </span>
                            )}
                        </label>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                            <label
                                htmlFor="username"
                                className="flex flex-col w-full font-Manrope font-semibold text-sm text-[#272D37]"
                            >
                                Username{" "}
                                <input
                                    type="text"
                                    id="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="mt-2 border border-[#DAE0E6] px-4 py-3 rounded-md font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                                    placeholder="Enter your Username"
                                />
                                {errors.username && (
                                    <span className="text-red-500 text-xs font-Manrope mt-2">
                                        {errors.username}
                                    </span>
                                )}
                            </label>
                            <label
                                htmlFor="phone"
                                className="flex flex-col w-full font-Manrope font-semibold text-sm text-[#272D37]"
                            >
                                Phone{" "}
                                <input
                                    type="tel"
                                    id="phone"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="mt-2 border border-[#DAE0E6] px-4 py-3 rounded-md font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                                    placeholder="Enter your Phone"
                                />
                                {errors.phone && (
                                    <span className="text-red-500 text-xs font-Manrope mt-2">
                                        {errors.phone}
                                    </span>
                                )}
                            </label>
                        </div>

                        <label
                            htmlFor="email"
                            className="flex flex-col w-full font-Manrope font-semibold text-sm text-[#272D37]"
                        >
                            Email{" "}
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-2 border border-[#DAE0E6] px-4 py-3 rounded-md font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                                placeholder="Enter your Email"
                            />
                            {errors.email && (
                                <span className="text-red-500 text-xs font-Manrope mt-2">
                                    {errors.email}
                                </span>
                            )}
                        </label>

                        <label
                            htmlFor="password"
                            className="flex flex-col w-full font-Manrope font-semibold text-sm text-[#272D37]"
                        >
                            Password{" "}
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-2 border border-[#DAE0E6] px-4 py-3 rounded-md font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                                placeholder="Enter your Password"
                            />
                            {errors.password && (
                                <span className="text-red-500 text-xs font-Manrope mt-2">
                                    {errors.password}
                                </span>
                            )}
                        </label>

                        <label
                            htmlFor="country"
                            className="flex flex-col w-full font-Manrope font-semibold text-sm text-[#272D37]"
                        >
                            Country{" "}
                            <select
                                id="country"
                                value={selectedCountry?.id || ""}
                                onChange={(e) => {
                                    const nextCountry = countries.find(
                                        (country) => String(country.id) === e.target.value
                                    );
                                    setSelectedCountry(nextCountry || null);
                                    setSelectedState("");
                                }}
                                className="mt-2 border border-[#DAE0E6] px-4 py-3 rounded-md font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                            >
                                <option value="">Select your country</option>
                                {loadingCountries ? (
                                    <option value="" disabled>
                                        Loading countries...
                                    </option>
                                ) : (
                                    countries.map((country) => (
                                        <option key={country.id} value={country.id}>
                                            {country.name} ({country.iso3})
                                        </option>
                                    ))
                                )}
                            </select>
                            {errors.country && (
                                <span className="text-red-500 text-xs font-Manrope mt-2">
                                    {errors.country}
                                </span>
                            )}
                        </label>

                        <label
                            htmlFor="state"
                            className="flex flex-col w-full font-Manrope font-semibold text-sm text-[#272D37]"
                        >
                            State{" "}
                            <select
                                id="state"
                                value={selectedState}
                                onChange={(e) => setSelectedState(e.target.value)}
                                disabled={!selectedCountry}
                                className="mt-2 border border-[#DAE0E6] px-4 py-3 rounded-md font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                            >
                                <option value="">
                                    {selectedCountry ? "Select state" : "Choose country first"}
                                </option>
                                {renderStateOptions()}
                            </select>
                            {errors.state && (
                                <span className="text-red-500 text-xs font-Manrope mt-2">
                                    {errors.state}
                                </span>
                            )}
                        </label>

                        <label
                            htmlFor="referralCode"
                            className="flex flex-col w-full font-Manrope font-semibold text-sm text-[#272D37]"
                        >
                            Referral Code (Optional){" "}
                            <input
                                type="text"
                                id="referralCode"
                                value={referralCode}
                                onChange={(e) => setReferralCode(e.target.value)}
                                className="mt-2 border border-[#DAE0E6] px-4 py-3 rounded-md font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                                placeholder="Enter referral code"
                            />
                        </label>
                    </div>
                </div>
                <div className="mt-6 p-4 bg-[#fff] flex flex-col items-center gap-4">
                    <p className="text-xs text-[#5F6D7E] text-center font-Manrope">
                        By signing up, you agree to the{" "}
                        <Link to="/terms" className="text-[#317333] hover:underline">
                            Terms
                        </Link>{" "}
                        and{" "}
                        <Link to="/privacy" className="text-[#317333] hover:underline">
                            Privacy Policy
                        </Link>
                        .
                    </p>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="bg-[#317333] rounded-md w-full py-3 font-Manrope text-[#fff] font-semibold text-base flex justify-center items-center"
                    >
                        {loading ? (
                            <img src={load} className="w-5" alt="Loading" />
                        ) : (
                            "Sign up"
                        )}
                    </button>
                    <p className="mt-2 font-normal text-sm font-Manrope text-[#5F6D7E]">
                        Already have an account?{" "}
                        <Link
                            to="/sign-in"
                            className="font-Manrope text-[#317333] font-bold text-[15px]"
                        >
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Signup;
